<script>
  import { onMount } from 'svelte';
  import { generator_path } from '../stores';
  import { get } from 'svelte/store';
  import Blink from '../elements/Blink.svelte';
  export let font = {}; // Font object with properties like file_name and file_url
  export let previewText = "Your Text Here";
  export let tracking;
  export let lineSpace;
  export let fontScale;
  export let bgColor;
  export let textColor;
  
  let imageUrl = "";
  let loading = true;
  let error = false;
  let debugInfo = "";
  let imageWidth = 0;
  let imageHeight = 0;
  $: if ((font && (font.file_name || font.file_url)) || (previewText && imageUrl)) {
    generatePreview();
  }
  
  function generatePreview() {
    loading = true;
    error = false;
    
    try {
      // Ensure we have a valid font object
      if (!font.file_url) {
        throw new Error('Font file_url is missing');
      }
      
      // Generate a unique timestamp to prevent browser caching
      const timestamp = new Date().getTime();
      
      // Create the preview image URL with all required parameters
      
      // Check if this is an S3 URL (which needs special handling)
      const isS3Url = font.file_url && (font.file_url.includes('amazonaws.com') || font.file_url.includes('X-Amz-Signature'));
      
      imageUrl = `${get(generator_path)}generate_preview.php?` + 
        `font_file_name=${encodeURIComponent(font.file_name)}` +
        `&file_url=${encodeURIComponent(font.file_url)}` +
        `&preview_text=${encodeURIComponent(previewText)}` +
        `&tracking=${tracking}` +
        `&line_space=${lineSpace}` +
        `&font_scale=${fontScale}` +
        `&bg_color=${bgColor}` +
        `&text_color=${textColor}` +
        `&text_align=center` +
        `&transparent=1` +
        `&padding=10` +
        `&resolution=3` +
        `&t=${timestamp}` +
        `&debug=1`;
      
      debugInfo = `Attempting to load: ${font.file_name}`;
    } catch (err) {
      console.error(`[FontPreview] Error creating preview URL: ${err.message}`);
      error = true;
      debugInfo = `Error: ${err.message}`;
    }
  }
  
  function handleImageError(event) {
    console.error(`[FontPreview] Failed to load preview for ${font.file_name}`);
    error = true;
    
    // Get more detailed error information from the server
    fetch(`${imageUrl}&debug_output=json`)
      .then(response => response.json())
      .then(data => {
        if (data && data.debug) {
          console.log(`[FontPreview] Debug info for ${font.file_name}:`, data);
          debugInfo = data.message || 'Failed to load preview';
        } else {
          debugInfo = 'Failed to load preview';
        }
      })
      .catch(err => {
        debugInfo = `Error: ${err.message}`;
      });
  }
  
  function handleImageLoad(event) {
    // Get natural dimensions of the loaded image
    if (event.target) {
      imageWidth = event.target.naturalWidth / 3;  // Set to one-third the natural size for 3x images
      imageHeight = event.target.naturalHeight / 3;
    }
    loading = false;
    debugInfo = `Successfully loaded: ${font.file_name}`;
  }
  
  onMount(() => {
    if (font && (font.file_name || font.file_url)) {
      generatePreview();
    }
  });
</script>

{#if loading && !error}
  <div class="preview-loading-indicator"><Blink>Loading preview...</Blink></div>
{/if}

{#if error}
  <div class="preview-error-message">
    Error loading preview
    <button class="action-button" on:click|stopPropagation={generatePreview}>Retry loading</button>
  </div>
{/if}

{#if imageUrl}
  <img 
    src={imageUrl} 
    alt="Font preview of {font.preferred_name || font.display_name || font.postscript_name}"
    class="preview-image"
    class:loading={loading}
    on:error={handleImageError}
    on:load={handleImageLoad}
    style="opacity: {loading || error ? 0 : 1}; width: {imageWidth}px; height: {imageHeight}px;"
  />
{/if}

<style>

  .preview-image {
    /* Explicit sizing will be set inline using style attribute */
    object-fit: contain;
    transition: opacity var(--transition-props);
  }
  .preview-image.loading {
    position: absolute;
  }
  
  /* Ensure parent container in zoomed state handles the image properly */
  :global(.zoomed .preview-image-wrapper) {
    height: 100%;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  
  :global(.zoomed .preview-image) {
    /* When zoomed, show at full resolution */
    width: auto !important;
    height: auto !important;
    max-width: 90%;
    max-height: 80%;
  }

  .preview-loading-indicator, .preview-error-message {
    opacity: 0.7;
    width: 100%;
    padding: 4rem;
    font-size: 2.4rem;
  }
  
  .preview-error-message {
    color: var(--c-accent-orange);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
  
  .action-button {
    padding: 0.5rem 1rem;
    margin-top: 1rem;
    margin-bottom: -2rem;
    background-color: var(--c-accent);
    color: white;
    border: none;
    border-radius: 2rem;
    cursor: pointer;
    font-family: var(--PensumSans);
    font-size: 1.1rem;
  }
  
  .action-button:hover {
    opacity: 0.9;
  }

</style> 